import { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import MDBox from 'components/MDBox';
import MDAvatar from 'components/MDAvatar';
import MDBadge from 'components/MDBadge';
import Dialog from '@mui/material/Dialog';
import { config } from 'Constants';

// Let's import our components similar to how lead Card does it
import ProductionCardBody from './ProductionCardBody';
import ProductionCardFooter from './ProductionCardFooter';
import SingleProduction from 'layouts/production/Pages/SingleProduction';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import { getDaysPassedColor } from 'layouts/production/helpers/dateHelpers';

function ProductionCard({
    productionName,
    status,
    createdAt,
    aaroCustomerName,
    responsibles,
    isPinned,
    productionID,
    statusTags,
    taskList,
    tags,
    aaroFields,
}) {
    const [openProduction, setOpenProduction] = useState(false);
    const navigate = useNavigate();
    const { completionPercentage, taskCount } = taskList || {};

    // Use the provided completionPercentage directly or default to 0
    const completionPercentageValue = typeof completionPercentage === 'number' ? completionPercentage : 0;

    // Use the provided taskCount directly or default to 0
    const taskCountValue = typeof taskCount === 'number' ? taskCount : 0;

    // Calculate completed tasks count based on completion percentage and total count
    const completedTasksCount = Math.ceil(taskCountValue * (completionPercentageValue / 100));

    // Determine progress color based on completion percentage
    const getProgressColor = (value) => {
        if (value < 30) return 'error';
        if (value < 70) return 'warning';
        return 'success';
    };

    // Calculate days passed since creation date (OlsTar)
    const calculateDaysPassed = () => {
        if (!aaroFields?.OlsTar) return null;

        const creationDate = new Date(aaroFields.OlsTar);
        const today = new Date();

        // Clear time portions for accurate day calculation
        creationDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        const diffTime = today - creationDate;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        return diffDays > 0 ? diffDays : 0; // Ensure we don't show negative days
    };

    // Format the days passed text
    const formatDaysPassedText = (daysPassed) => {
        if (aaroFields?.Kapali) return 'kapatılmış';
        if (daysPassed === null) return 'Tarih yok';
        return `${daysPassed} gün`;
    };

    const daysPassed = calculateDaysPassed();
    const daysPassedColor = getDaysPassedColor(aaroFields, daysPassed);

    // Handle dialog close
    const handleCloseProduction = () => {
        setOpenProduction(false);
    };

    // Navigate to full page view
    const handleOpenFullPage = () => {
        navigate(`/orders/${productionID}`);
        setOpenProduction(false);
    };

    // Render responsible users avatars - exactly like the lead Card
    const renderMembers = responsibles?.map((member, key) => (
        <MDAvatar
            key={`image-${key}`}
            src={member?.profilePicture || config.random.ANONYMOUS_USER_IMAGE}
            alt={member?.name}
            size="xs"
            sx={({ borders, palette }) => ({
                border: `${borders.borderWidth[2]} solid ${palette.white.main}`,
                cursor: 'pointer',
                position: 'relative',
                ml: -1,
                mr: 0,
                '&:hover, &:focus': {
                    zIndex: '10',
                },
            })}
        />
    ));

    return (
        <MDBox onDoubleClick={() => setOpenProduction(true)}>
            {/* Header - using MDBadge like in the lead Card */}
            <MDBox display="flex" alignItems="center" color="text" justifyContent="space-between">
                <MDBox display="flex" gap={1}>
                    <MDBadge
                        size="xs"
                        color={daysPassedColor}
                        badgeContent={formatDaysPassedText(daysPassed)}
                        container
                    />
                </MDBox>
                {/* Render members en sağ üste taşındı */}
                <MDBox display="flex">{renderMembers}</MDBox>
            </MDBox>

            {/* CardBody with our props */}
            <ProductionCardBody productionName={productionName} aaroFields={aaroFields} tags={tags} />

            {/* CardFooter with progress info */}
            <ProductionCardFooter
                createdAt={createdAt}
                onView={() => navigate(`/orders/${productionID}`)}
                onEdit={() => navigate(`/orders/${productionID}`)}
                completionPercentage={completionPercentageValue}
                taskCount={taskCountValue}
                completedTasksCount={completedTasksCount}
                getProgressColor={getProgressColor}
                aaroFields={aaroFields}
            />

            {/* Dialog for SingleProduction */}
            <Dialog
                onClose={handleCloseProduction}
                open={openProduction}
                fullWidth={true}
                maxWidth="xl"
                sx={{ '.MuiDialog-paper': { height: '90vh' } }}
            >
                <DialogTitle>
                    {productionName}
                    <MDBox position="absolute" top={8} right={8} display="flex">
                        <Tooltip title="Genişlet" arrow>
                            <IconButton onClick={handleOpenFullPage} sx={{ mr: 1 }}>
                                <FullscreenIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Kapat" arrow>
                            <IconButton onClick={handleCloseProduction}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </MDBox>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 5, backgroundColor: '#dee2e6' }}>
                    <SingleProduction productionId={productionID} isDialog={true} />
                </DialogContent>
            </Dialog>
        </MDBox>
    );
}
ProductionCard.propTypes = {
    productionName: PropTypes.string.isRequired,
    status: PropTypes.object,
    createdAt: PropTypes.string,
    aaroCustomerName: PropTypes.string,
    responsibles: PropTypes.array,
    isPinned: PropTypes.bool,
    productionID: PropTypes.string.isRequired,
    statusTags: PropTypes.array,
    taskList: PropTypes.object, // Updated: taskList is an object, not an array
};
ProductionCard.defaultProps = {
    status: null,
    createdAt: '',
    aaroCustomerName: '',
    responsibles: [],
    isPinned: false,
    statusTags: [],
    taskList: null, // Updated default to null instead of empty array
};
export default ProductionCard;
